import { convertDateToTimestamp } from "utility/firebase";

import type { LegacyPortalLiveCommerceProduct } from "@spwn-portal/grpc";
import type { ProductData } from "@spwn/types/firebase/firestore";

/** 在庫を追跡していない商品はカート上限が要らないので大きめの値を入れる */
const UNLIMITED_STOCK = 9999;

/**
 * Shopify の商品を、既存 UI がそのまま扱える ProductData へ変換する。
 *
 * UI を変えずに商品データだけ Shopify へ寄せるための暫定マッピング。
 * - Shopify の Product = ProductData の class（バリエーションのグループ）
 * - Shopify の ProductVariant = ProductData 1件（subClass / subClassName）
 */
export const toProductData = (
  product: LegacyPortalLiveCommerceProduct,
  productIndex: number,
  eventId: string
): ProductData[] => {
  // 表示期間の判定 (isDisplayPeriod) を通すため、十分に広い期間を入れる。
  // 販売期間は Shopify 側で管理する想定なので SPWN 側では絞らない。
  const releaseDateTime = convertDateToTimestamp(new Date(0));
  const farFuture = convertDateToTimestamp(new Date(Date.UTC(2100, 0, 1)));

  return product.variants.map((variant, variantIndex) => {
    // Shopify は在庫を追跡していない場合や「在庫切れでも販売を継続」の設定で
    // quantityAvailable が 0 以下 / 未設定になる。購入可否の正は availableForSale なので、
    // 数量が取れないときは上限なしとして扱う。
    const trackedStock =
      variant.quantityAvailable !== undefined && variant.quantityAvailable > 0
        ? variant.quantityAvailable
        : UNLIMITED_STOCK;
    const stock = variant.availableForSale ? trackedStock : 0;

    // `satisfies` は legacy の prettier (2.3) が解釈できないため、型注釈で同じ検査をかける
    const productData: ProductData = {
      // Shopify の variant GID を SPWN 側の商品 ID として使う。
      // convertArrayToDict が _id をキーにするため必須。
      _id: variant.id,
      eid: eventId,
      eventTitle: "",
      eventLongTitle: "",
      productType: "goods",
      name: product.title,
      className: product.title,
      class: productIndex + 1,
      subClass: variantIndex + 1,
      // バリエーションが1つだけの商品は選択肢を出さない
      subClassName: product.variants.length > 1 ? variant.title : undefined,
      description: product.descriptionHtml,
      // 商品詳細 (StreamingLiveCommerceDisplayInfo) は specification を表示するため両方に入れる
      specification: product.descriptionHtml,
      price_jpy: variant.priceAmount,
      displayPrice: variant.priceAmount,
      thumbnail: product.featuredImageUrl,
      classThumbnails: product.imageUrls,
      display: true,
      place: "",
      priority: productIndex * 100 + variantIndex,
      total: stock,
      cartStock: stock,
      // 売り切れの sentinel は -1。0 ではない
      // (EventGoodsList / EventGoodsItemInfo が remaining === -1 で判定する)
      remaining: variant.availableForSale ? undefined : -1,
      releaseDateTime,
      closeDateTime: farFuture,
      expiredDateTime: farFuture,
      saleType: "liveCommerce",
      // ライブコマースの一覧は isShippingItem (isAddressRequired || isAddressRequiredNoFee)
      // を通った商品しか表示されない。配送は Shopify 側で管理し SPWN 側で送料を
      // 上乗せしないため、送料なしの住所必須 (isAddressRequiredNoFee) を立てる。
      isAddressRequired: false,
      isAddressRequiredNoFee: true,
    };
    return productData;
  });
};
